import useSWR, { SWRConfiguration } from "swr";
import { calcUrl, SWRObjectKey } from "@/features/ui/helpers/fetcher-helpers";
import useFetcher from "@/utils/use-fetcher";
import { ProductPrice } from "@/features/types";
import {
  CommissionType,
  PriceAdjustedBy,
} from "@/features/line-sheet-sets/line-sheet-set.types";

// 옵션(사이즈) 하나에 들어온 주문 — 이 부티크에 제출된 모든 바이어 오더시트를 합친 값
export interface AllOrderedProductOption {
  productOptionId: number | null;
  name: string;
  quantity: number;
  // 부티크가 넣은 확정 수량의 합. 아직 아무도 확정하지 않았으면 null(미확정)
  confirmedQuantity: number | null;
  // 오더 한 건을 볼 때 확정 수량을 저장할 오더시트(productOptionId 와 함께 보낸다)
  orderSheetId: number | null;
}

export interface AllOrderedProduct {
  id: number;
  lineSheetId: number;
  brandName: string | null;
  name: string | null;
  modelNumber: string | null;
  sku: string | null;
  colorName: string | null;
  colorCode: string | null;
  season: string | null;
  imageUrls: string[];
  alternativeImageUrls: string[];
  // 라인시트 상품 그대로의 가격·가격조정(특정 바이어의 오더 조건이 아니다)
  supplyPrice: ProductPrice | null;
  costPrice: ProductPrice | null;
  retailPrice: ProductPrice | null;
  priceAdjustedBy: PriceAdjustedBy | null;
  // 오더 한 건을 볼 때만 — 그 오더에 지금 저장된 확정 가격조정·확정 단가·기준가(오더시트 화면과 같은 값)
  orderPrice?: AllOrderedProductOrderPrice | null;
  stockQuantityWithOptionList: { name: string; quantity: number | null }[];
  orderQuantityWithOptionList: AllOrderedProductOption[];
}

export interface AllOrderedProductOrderPrice {
  priceAdjustedBy: PriceAdjustedBy | null;
  price: ProductPrice | null;
  priceWithoutCondition: ProductPrice | null;
}

export interface AllOrderedQuantitySummary {
  quantity: number;
  confirmedQuantity: number;
}

export interface AllOrderedLineSheet extends AllOrderedQuantitySummary {
  id: number;
  name: string | null;
  productCount: number;
}

export interface GetBoutiqueAllOrderedProductsRequest {
  // 주면 그 라인시트에 들어온 주문만 합친다
  lineSheetId?: number;
  // 주면 그 오더(오더시트세트) 한 건만 본다
  orderSheetSetId?: number;
}

// 오더 한 건을 볼 때만 온다 — 확정 수량을 넣을 수 있는지
export interface AllOrderedProductsOrder {
  id: number;
  status: string | null;
  confirmable: boolean;
  // 확정 단가 미리보기(오더시트 화면 calcPrice 와 같은 식)에 쓰는 세트 통화·수수료
  currency: string;
  commission: number | null;
  commissionType: CommissionType | null;
}

export interface GetBoutiqueAllOrderedProductsResponse {
  products: AllOrderedProduct[];
  totalCount: number;
  lineSheets: AllOrderedLineSheet[];
  summary: AllOrderedQuantitySummary;
  order: AllOrderedProductsOrder | null;
}

export interface GetBoutiqueAllOrderedProductsPathParameter {
  boutiqueId: number;
  lineSheetSetId: number;
}

export const getBoutiqueAllOrderedProductsTemplateUrl =
  "/boutiques/:boutiqueId/pathpick/line-sheet-sets/:lineSheetSetId/all-ordered-products";

export function getGetBoutiqueAllOrderedProductsKey(
  parameter: GetBoutiqueAllOrderedProductsPathParameter | null,
  request: GetBoutiqueAllOrderedProductsRequest
): SWRObjectKey<GetBoutiqueAllOrderedProductsRequest> | null {
  if (parameter === null) {
    return null;
  }
  return {
    url: calcUrl(getBoutiqueAllOrderedProductsTemplateUrl, parameter),
    params: request,
  };
}

/**
 * (부티크) 라인시트세트에 들어온 모든 바이어의 오더를 상품·옵션별로 합쳐 받는다.
 * 세트 전체를 한 번 받아 두고 라인시트별로 거르는 건 화면에서 한다 — 라인시트를 바꿀 때마다 다시 부르지 않는다.
 */
export default function useGetBoutiqueAllOrderedProducts(
  parameter: GetBoutiqueAllOrderedProductsPathParameter | null,
  request: GetBoutiqueAllOrderedProductsRequest = {},
  config?: SWRConfiguration<GetBoutiqueAllOrderedProductsResponse>
) {
  const fetcher = useFetcher<
    GetBoutiqueAllOrderedProductsRequest,
    GetBoutiqueAllOrderedProductsResponse
  >();
  return useSWR(
    getGetBoutiqueAllOrderedProductsKey(parameter, request),
    fetcher,
    config
  );
}
